import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Box, Stack, TextField, Button, CircularProgress } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { BatteryDto, BatteryUpdateRequest } from "../../generated/api-client";
import { AppClient as ClientService } from "../../services/client.service";
import { Center } from "../Center";
import { useToast } from "../ToastContext";

interface BatteryEditFormProps {
  battery: BatteryDto;
  onSaveSuccess?: (updatedBattery: BatteryDto) => void;
  onCancel?: () => void;
}

export function BatteryEditForm({
  battery,
  onSaveSuccess,
  onCancel,
}: BatteryEditFormProps) {
  const intl = useIntl();
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<BatteryUpdateRequest>(() => {
    const updateRequest = new BatteryUpdateRequest();
    updateRequest.groupId = battery.groupId || "";
    updateRequest.serialNumber = battery.serialNumber || "";
    updateRequest.stateOfCharge = battery.stateOfCharge;
    updateRequest.fwVersion = battery.fwVersion || "";
    return updateRequest;
  });

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updatedBattery = await ClientService.getInstance().batteriesPUT(
        battery.id,
        ClientService.version,
        formData
      );
      showToast(
        intl.formatMessage({ id: "batterySavedSuccessfully" }),
        "success"
      );
      onSaveSuccess?.(updatedBattery);
    } catch (err: any) {
      setError(
        err.message || intl.formatMessage({ id: "failedToSaveBattery" })
      );
    } finally {
      setSaving(false);
    }
  };

  if (saving) {
    return (
      <Box sx={{ my: "5rem" }}>
        <Center>
          <CircularProgress size={60} />
        </Center>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Stack spacing={2}>
        {error && (
          <Box sx={{ color: "error.main", fontSize: "0.875rem" }}>{error}</Box>
        )}
        <TextField
          label={intl.formatMessage({ id: "serialNumber" })}
          value={formData.serialNumber || ""}
          onChange={(e) => {
            const updated = new BatteryUpdateRequest(formData);
            updated.serialNumber = e.target.value;
            setFormData(updated);
          }}
          fullWidth
          required
        />
        <TextField
          label={intl.formatMessage({ id: "stateOfCharge" })}
          type="number"
          value={formData.stateOfCharge}
          onChange={(e) => {
            const updated = new BatteryUpdateRequest(formData);
            updated.stateOfCharge = parseFloat(e.target.value) || 0;
            setFormData(updated);
          }}
          fullWidth
          required
        />
        <TextField
          label={intl.formatMessage({ id: "fwVersion" })}
          value={formData.fwVersion || ""}
          onChange={(e) => {
            const updated = new BatteryUpdateRequest(formData);
            updated.fwVersion = e.target.value;
            setFormData(updated);
          }}
          fullWidth
        />
        <Box sx={{ display: "flex", justifyContent: "right", gap: 1 }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel} disabled={saving}>
              {intl.formatMessage({ id: "deleteCancelLabel" })}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {intl.formatMessage({ id: "save" })}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
